import type { GraphPath } from '@ipfabric/utils';
import {
  type GraphState,
  isTopologyGraphMetadata,
} from 'components/Graph/GraphState';
import { selectNodesStableData } from 'components/Graph/model/selectors/selectNodesStableData';
import { createSelector } from 'reselect';
import { makeMemoizeObject } from 'utils/memoize/makeMemoizeObject';
import { createScopedSelector } from 'utils/react/scopedSelector';
import { selectNotHiddenNodes } from '../selectNotHiddenNodes';

export interface GraphPathWarnings {
  emptyPaths: Set<GraphPath>;
  hiddenPaths: Set<GraphPath>;
  missingPaths: Set<GraphPath>;
}

const selectNonEmptyPaths: (s: GraphState) => Set<GraphPath> =
  createScopedSelector(() =>
    createSelector(selectNodesStableData, (nodes) =>
      Object.values(nodes).reduce((acc, node) => {
        if (node.parentPath !== null) {
          acc.add(node.parentPath);
        }
        return acc;
      }, new Set<GraphPath>()),
    ),
  );

const selectNonHiddenPaths: (s: GraphState) => Set<GraphPath> =
  createScopedSelector(() =>
    createSelector(selectNotHiddenNodes, (nodes) =>
      Object.values(nodes).reduce((acc, node) => {
        if (node.parentPath !== null) {
          acc.add(node.parentPath);
        }
        return acc;
      }, new Set<GraphPath>()),
    ),
  );

export const selectPathWarnings: (s: GraphState) => GraphPathWarnings =
  createScopedSelector(() => {
    const memoizeObject = makeMemoizeObject<GraphPathWarnings>();
    return createSelector(
      selectNonEmptyPaths,
      selectNonHiddenPaths,
      (s) => s.graphMetadata,
      // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TBD eventually cleanup
      (nonEmptyPaths, nonHiddenPaths, metadata) => {
        const result: GraphPathWarnings = {
          missingPaths: new Set(),
          hiddenPaths: new Set(),
          emptyPaths: new Set(),
        };
        if (isTopologyGraphMetadata(metadata)) {
          const existingPaths = new Set(Object.keys(metadata.paths));
          for (const path of metadata.parameters.paths) {
            if (!existingPaths.has(path)) {
              result.missingPaths.add(path);
            }
            if (!nonEmptyPaths.has(path)) {
              result.emptyPaths.add(path);
            }
            if (!nonHiddenPaths.has(path)) {
              result.hiddenPaths.add(path);
            }
          }
        }

        return memoizeObject(result);
      },
    );
  });
